import { useState, useEffect } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { SectionHero } from '../components/sections/SectionHero';
import { SEO } from '../components/SEO';
import { PAGE_SEO } from '../config/seoCopy';
import { motion } from 'framer-motion';
import { Loader2, ExternalLink, Calendar, Newspaper } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

const PremsaPage = () => {
  const { language } = useLanguage();
  const [links, setLinks] = useState([]);
  const [loading, setLoading] = useState(true);

  const translations = {
    ca: {
      title: "Premsa",
      subtitle: "Mencions i notícies sobre UGOLF ARAVELL",
      noLinks: "No hi ha mencions de premsa disponibles.",
      readMore: "Llegir més",
      source: "Font"
    },
    es: {
      title: "Prensa",
      subtitle: "Menciones y noticias sobre UGOLF ARAVELL",
      noLinks: "No hay menciones de prensa disponibles.",
      readMore: "Leer más",
      source: "Fuente"
    },
    fr: {
      title: "Presse",
      subtitle: "Mentions et actualités sur UGOLF ARAVELL",
      noLinks: "Aucune mention de presse disponible.",
      readMore: "Lire plus",
      source: "Source"
    },
    en: {
      title: "Press",
      subtitle: "Mentions and news about UGOLF ARAVELL",
      noLinks: "No press mentions available.",
      readMore: "Read more",
      source: "Source"
    }
  };

  const t = translations[language] || translations.ca;

  useEffect(() => {
    const fetchLinks = async () => {
      try {
        const response = await fetch(`${API_URL}/api/press`);
        if (response.ok) {
          const data = await response.json();
          setLinks(data.links || []);
        }
      } catch (error) {
        console.error('Failed to fetch press links:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLinks();
  }, []);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString(language === 'en' ? 'en-GB' : `${language}-${language.toUpperCase()}`, {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  return (
    <main data-testid="premsa-page">
      <SEO title={PAGE_SEO.premsa.title} description={PAGE_SEO.premsa.description} />
      <SectionHero
        image="https://images.unsplash.com/photo-1504711434969-e33886168f5c?q=80&w=1920"
        title={t.title}
        subtitle={t.subtitle}
      />

      <section className="section-spacing bg-white">
        <div className="container-custom">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-[#1A4D2E]" />
            </div>
          ) : links.length === 0 ? (
            <div className="text-center py-12">
              <Newspaper className="w-12 h-12 text-[#CCCCCC] mx-auto mb-4" />
              <p className="text-[#888888]">{t.noLinks}</p>
            </div>
          ) : (
            <div className="space-y-6 max-w-4xl mx-auto">
              {links.map((link, index) => (
                <motion.a
                  key={link.id}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="block bg-white border border-[#E5E5E5] rounded-lg overflow-hidden hover:border-[#1A4D2E] hover:shadow-lg transition-all group"
                  data-testid={`press-link-${link.id}`}
                >
                  <div className="flex flex-col md:flex-row">
                    {/* Image */}
                    {link.image && (
                      <div className="md:w-64 lg:w-80 flex-shrink-0">
                        <div className="aspect-[16/9] md:aspect-auto md:h-full bg-[#F0F2EF]">
                          <img
                            src={link.image}
                            alt={link.title || ''}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            onError={(e) => e.target.style.display = 'none'}
                          />
                        </div>
                      </div>
                    )}
                    
                    {/* Content */}
                    <div className="flex-1 p-5 md:p-6">
                      {/* Date and Source */}
                      <div className="flex items-center gap-3 text-xs text-[#888888] mb-3">
                        <span className="flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(link.date)}
                        </span>
                        {link.site_name && (
                          <>
                            <span>•</span>
                            <span className="font-medium text-[#1A4D2E]">{link.site_name}</span>
                          </>
                        )}
                      </div>
                      
                      {/* Title */}
                      {link.title && (
                        <h3 
                          className="font-display text-lg md:text-xl font-medium text-[#333333] group-hover:text-[#1A4D2E] transition-colors mb-2 line-clamp-2"
                          dangerouslySetInnerHTML={{ __html: link.title }}
                        />
                      )}
                      
                      {/* Description */}
                      {link.description && (
                        <p className="text-[#666666] text-sm line-clamp-2 mb-4">
                          {link.description}
                        </p>
                      )}
                      
                      {/* Read more */}
                      <span className="inline-flex items-center gap-1 text-xs font-semibold text-[#1A4D2E] uppercase tracking-wide group-hover:gap-2 transition-all">
                        {t.readMore}
                        <ExternalLink className="w-3 h-3" />
                      </span>
                    </div>
                  </div>
                </motion.a>
              ))}
            </div>
          )}
        </div>
      </section>
    </main>
  );
};

export default PremsaPage;
